import { useState, useContext, useEffect } from "react";
import { Eye, EyeOff, Mail, Smartphone, RefreshCw ,Home} from "lucide-react";
import API from "../../api/axios";
import { AuthContext } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// Logo import
import logoImg from "../../assets/logo.jpeg";

// Direct imports from MasterCssClass.js
import {
  masterAuthRoot,
  masterAuthGlowLeft,
  masterAuthGlowRight,
  masterAuthErrorBox,
  masterAuthSuccessBox,
  masterAuthLabel,
  masterAuthInput,
  masterAuthSubmitBtn,
} from "../../GlobalComponents/MasterCssClass";

const Login = () => {
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [registerRole, setRegisterRole] = useState("student");
  const [belongsToInst, setBelongsToInst] = useState(false);
  const [examsMaster, setExamsMaster] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [verifiedOrgName, setVerifiedOrgName] = useState("");
  // Password view toggle states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // --- MOBILE LANDING & LOGIN METHOD STATE ---
  const [showMobileLanding, setShowMobileLanding] = useState(true);
  const [loginMethod, setLoginMethod] = useState("email"); // "email" or "mobile"
  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaCode, setCaptchaCode] = useState("8r4apb"); // clean lowercase alphanumeric captcha

  // --- LOADING STATE ---
  const [isLoading, setIsLoading] = useState(false);

  // --- REFERRAL & TENANT EXAM STATES ---
  const [isReferralVerified, setIsReferralVerified] = useState(false);
  const [verifiedTenantKey, setVerifiedTenantKey] = useState("");
  const [orgExams, setOrgExams] = useState([]);
  const [verifyingCode, setVerifyingCode] = useState(false);

  // --- EMAIL OTP VERIFICATION STATES ---
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [emailOtp, setEmailOtp] = useState("");

  // --- FORGOT PASSWORD STATES ---
  const [isForgotMode, setIsForgotMode] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1: Enter Email, 2: Enter OTP, 3: New Password
  const [forgotEmail, setForgotEmail] = useState("");
  const [maskedEmailDisplay, setMaskedEmailDisplay] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  // Fixed Blackboard background image URL
  const blackboardBgUrl = "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?q=80&w=1000&auto=format&fit=crop";

  const [form, setForm] = useState({
    name: "",
    phone: "",
    userid: "",
    password: "",
    confirmPassword: "",
    referredBy: "",
    selectedExams: [],
  });

  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  // Automatically generate a new captcha on initial mount and mode switch
  useEffect(() => {
    generateNewCaptcha();
  }, [isLoginMode, isForgotMode, forgotStep]);

  useEffect(() => {
    const fetchExams = async () => {
      if (!isLoginMode) {
        try {
          const res = await API.get("/student/exam-options");
          setExamsMaster(Array.isArray(res.data?.data) ? res.data.data : []);
        } catch (err) {
          console.error("Could not load default exams", err);
        }
      }
    };
    fetchExams();
  }, [isLoginMode]);

  const handleExamSelect = (examCode) => {
    setForm((prev) => {
      const isAlreadySelected = prev.selectedExams.includes(examCode);
      const updatedExams = isAlreadySelected
        ? prev.selectedExams.filter((id) => id !== examCode)
        : [...prev.selectedExams, examCode];
      return { ...prev, selectedExams: updatedExams };
    });
  };

 const handleVerifyReferral = async () => {
    if (!form.referredBy.trim()) {
      setError("Please enter an institution referral code to verify.");
      return;
    }
    setError("");
    setSuccess("");
    setVerifyingCode(true);

    try {
      const { data } = await API.post("/auth/verify-referral", {
        referralCode: form.referredBy,
      });

      setVerifiedTenantKey(data.tenantKey);
      setVerifiedOrgName(data.orgName || data.name || form.referredBy); // Store organization name
      setOrgExams(data.exams || []);
      setIsReferralVerified(true);
      setSuccess(`Verified for ${data.orgName || data.name || "Institution"}!`);
      
      setForm((prev) => ({ ...prev, selectedExams: [] }));
    } catch (err) {
      setIsReferralVerified(false);
      setVerifiedTenantKey("");
      setVerifiedOrgName("");
      setOrgExams([]);
      setError(err.response?.data?.message || "Invalid or expired institution referral code.");
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleSendEmailOtp = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.userid)) {
      setError("Please enter a valid email address to verify.");
      return;
    }
    setError("");
    setSuccess("");
    setVerifyingEmail(true);

    try {
      const endpoint = registerRole === "organization" 
        ? "/auth/send-org-registration-otp" 
        : "/student/send-registration-otp";

      await API.post(endpoint, { email: form.userid });
      setShowOtpInput(true);
      setSuccess("OTP sent to your email successfully!");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send OTP.");
    } finally {
      setVerifyingEmail(false);
    }
  };

  const handleVerifyEmailOtp = async () => {
    if (!emailOtp.trim()) {
      setError("Please enter the OTP.");
      return;
    }
    setError("");
    setSuccess("");
    setVerifyingEmail(true);

    try {
      const endpoint = registerRole === "organization" 
        ? "/auth/verify-org-registration-otp" 
        : "/student/verify-registration-otp";

      await API.post(endpoint, { email: form.userid, otp: emailOtp });
      setIsEmailVerified(true);
      setShowOtpInput(false);
      setSuccess("Email verified successfully!");
    } catch (err) {
      setError(err.response?.data?.message || "Invalid or expired OTP.");
    } finally {
      setVerifyingEmail(false);
    }
  };

  // --- FORGOT PASSWORD HANDLERS ---
  const handleForgotRequestOtp = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setForgotLoading(true);

    try {
      const { data } = await API.post("/auth/reset/forgot-password-request", {
        email: forgotEmail,
      });
      setMaskedEmailDisplay(data.maskedEmail);
      setForgotStep(2); // Move to OTP step
      setSuccess("OTP sent successfully to your email.");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send password reset OTP.");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleForgotVerifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setForgotLoading(true);

    try {
      await API.post("/auth/reset/verify-forgot-otp", {
        email: forgotEmail,
        otp: forgotOtp,
      });
      setForgotStep(3); // Move to New Password step
      setSuccess("OTP verified successfully! Please enter your new password.");
      generateNewCaptcha(); // Generate captcha here for the final step
    } catch (err) {
      setError(err.response?.data?.message || "Invalid or expired OTP code.");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    if (captchaInput !== captchaCode) {
      setError("Invalid Captcha code. Please try again.");
      generateNewCaptcha();
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError("Passwords do not match.");
      generateNewCaptcha();
      return;
    }

    setError("");
    setSuccess("");
    setForgotLoading(true);

    try {
      await API.post("/auth/reset/reset-password", {
        email: forgotEmail,
        newPassword: newPassword,
        confirmPassword: confirmNewPassword,
      });
      setSuccess("Password reset successfully! Redirecting to login...");
      setTimeout(() => {
        setIsForgotMode(false);
        setForgotStep(1);
        setForgotEmail("");
        setForgotOtp("");
        setNewPassword("");
        setConfirmNewPassword("");
        setSuccess("");
      }, 2500);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to reset password.");
      generateNewCaptcha();
    } finally {
      setForgotLoading(false);
    }
  };

  // Captcha generator
  const generateNewCaptcha = () => {
    const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
    let res = "";
    for (let i = 0; i < 6; i++) {
      res += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptchaCode(res);
    setCaptchaInput("");
  };

 const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (captchaInput !== captchaCode) {
      setError("Invalid Captcha code. Please try again.");
      generateNewCaptcha();
      return;
    }

    // Validation for login mode: if email method is selected, prevent login if user inputs a phone number
    if (isLoginMode && loginMethod === "email") {
      const phoneOnlyRegex = /^[0-9]{10}$/;
      if (phoneOnlyRegex.test(form.userid.trim())) {
        setError("Invalid User ID format. Please enter a valid email address.");
        generateNewCaptcha();
        return;
      }
    }

    if (!isLoginMode) {
      const phoneRegex = /^[0-9]{10}$/;
      if (!phoneRegex.test(form.phone)) {
        setError("Please enter a valid 10-digit mobile number.");
        generateNewCaptcha();
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.userid)) {
        setError("Please enter a valid email address.");
        generateNewCaptcha();
        return;
      }

      if (!isEmailVerified) {
        setError("Please verify your email address using OTP before registering.");
        generateNewCaptcha();
        return;
      }

      if (form.password !== form.confirmPassword) {
        setError("Passwords do not match. Please check and try again.");
        generateNewCaptcha();
        return;
      }

      if (registerRole === "student" && belongsToInst && !isReferralVerified) {
        setError("Please verify your institution referral code first.");
        generateNewCaptcha();
        return;
      }
    }

    setIsLoading(true);
    try {
      if (isLoginMode) {
        const { data } = await API.post("/auth/login", {
          userid: form.userid,
          password: form.password,
        });

        login(data);
        const innerUser = data?.user || {};
        const targetEmail = String(innerUser?.email || "").toLowerCase().trim();
        const targetRole = String(innerUser?.role || "").toLowerCase().trim();

        if (targetEmail === "medhashala@gmail.com" || targetRole === "admin") {
          navigate("/dashboard");
        } else if (targetRole === "organization") {
          navigate("/organization-dashboard");
        } else {
          navigate(data.redirectTo || "/student");
        }
      } else {
        const isInstitutionStudent = registerRole === "student" && belongsToInst;
        const endpoint = "/auth/register";

        await API.post(endpoint, {
          name: form.name.toUpperCase(),
          phone: form.phone,
          email: form.userid,
          password: form.password,
          role: registerRole,
          belongsToInstitution: isInstitutionStudent,
          referredBy: isInstitutionStudent ? form.referredBy.toUpperCase() : "",
          tenantKey: isInstitutionStudent ? verifiedTenantKey : "MED001",
          exams: registerRole === "student" ? form.selectedExams : [],
        });

        if (registerRole === "organization" || isInstitutionStudent) {
          setSuccess("Registration submitted successfully! You can sign in once approved.");
        } else {
          setSuccess("Registration successful! You can now sign in.");
        }
        setForm({
          name: "",
          phone: "",
          userid: "",
          password: "",
          confirmPassword: "",
          referredBy: "",
          selectedExams: [],
        });
        setBelongsToInst(false);
        setIsReferralVerified(false);
        setIsEmailVerified(false);
        setShowOtpInput(false);
        setEmailOtp("");
        setVerifiedTenantKey("");
        setOrgExams([]);
        generateNewCaptcha();

        setTimeout(() => {
          setIsLoginMode(true);
          setSuccess("");
        }, 2500);
      }
    } catch (err) {
      generateNewCaptcha();
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const serverMsg = (err.response?.data?.message || "").toLowerCase();

        // Specific custom frontend checks mapped to backend response hints
        if (status === 404 || serverMsg.includes("not found") || serverMsg.includes("user not exist") || serverMsg.includes("account not exist")) {
          setError("This account does not exist.");
        } else if (serverMsg.includes("password") || status === 401 && serverMsg.includes("pass")) {
          setError("Invalid password.");
        } else if (serverMsg.includes("user") || serverMsg.includes("email") || serverMsg.includes("id")) {
          setError("Invalid user ID.");
        } else {
          setError(err.response?.data?.message || "Authentication failed.");
        }
      } else {
        setError("Error processing server request.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={masterAuthRoot}>
      <div className={masterAuthGlowLeft}></div>
      <div className={masterAuthGlowRight}></div>

      {/* Main container */}
      <div className="relative z-10 flex h-screen w-screen flex-col md:flex-row overflow-hidden select-none bg-white" style={{ letterSpacing: "0.05em", fontFamily: "'Montserrat', 'Arial Narrow', sans-serif" }}>
        
      {/* LEFT PANEL */}
        <div 
          className={`absolute inset-0 md:relative md:inset-auto md:flex md:w-2/5 h-screen w-screen overflow-hidden bg-[#0b1b2b] p-6 sm:p-12 transition-all duration-300 ${
            showMobileLanding ? "flex z-30" : "hidden md:flex"
          }`}
          style={{
            backgroundImage: `url(${blackboardBgUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 bg-[#0b1b2b]/85"></div>

          <div className="relative z-10 h-full w-full max-w-sm mx-auto flex flex-col items-center justify-between py-6 sm:py-10 text-center">
            <div></div>

            <div className="space-y-4 flex flex-col items-center my-auto">
              <div className="relative group">
                <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-blue-600 to-indigo-500 opacity-75 blur-md group-hover:opacity-100 transition duration-500"></div>
                <div className="relative h-24 w-24 sm:h-28 sm:w-28 overflow-hidden rounded-3xl border-3 border-white bg-white shadow-2xl flex items-center justify-center">
                  <img
                    src={logoImg}
                    alt="Medhashala Logo"
                    className="h-full w-full object-cover transform hover:scale-105 transition duration-300"
                  />
                </div>
              </div>

              <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-[0.05em] drop-shadow-md">MEDHASHALA</h1>
              <div className="mx-auto h-1 w-12 rounded-full bg-[#F4B429]"></div>
              <p className="text-xs sm:text-sm font-semibold text-slate-200 tracking-[0.05em]">
                Your Path to Smarter Learning
              </p>
            </div>

            <div className="w-full px-4 shrink-0 pb-4">
              <button
                type="button"
                onClick={() => setShowMobileLanding(false)}
                className="w-full py-3.5 px-6 bg-[#F4B429] hover:bg-[#e0a320] text-[#0E3652] font-black text-sm rounded-full shadow-2xl transition-all md:hidden cursor-pointer tracking-[0.05em]"
              >
                Get Started
              </button>
            </div>

          </div>
        </div>

        {/* RIGHT PANEL */}
        <div 
          className={`absolute inset-0 md:relative md:inset-auto md:flex md:w-3/5 h-full w-full flex-col items-center justify-center bg-white px-4 sm:px-8 lg:px-16 overflow-hidden transition-all duration-300 ${
            !showMobileLanding ? "flex z-30" : "hidden md:flex"
          }`}
        >
          {/* Top Header: Back to Home (Left) & Logo (Right) */}
          <div className="absolute top-6 left-8 right-8 hidden md:flex items-center justify-between select-none">
            <button
              type="button"
              onClick={() => navigate("/home-about")}
              className="flex items-center gap-1.5 px-3 py-1 bg-slate-100 hover:bg-slate-200 text-[#0E3652] font-black text-xs rounded-xl transition-all cursor-pointer tracking-[0.05em] border border-slate-200 shadow-sm"
            >
              <Home size={14} /> Home
            </button>
            <div className="h-14 w-14 overflow-hidden rounded-2xl bg-white shadow-sm border border-slate-100 flex items-center justify-center">
              <img src={logoImg} alt="Medhashala Logo" className="h-full w-full object-cover" />
            </div>
          </div>

       {/* Conditional rendering for Login vs Register vs Forgot Password */}
          {isForgotMode ? (
            /* ================= FORGOT PASSWORD FLOW ================= */
            <div className="flex w-full max-w-md flex-col rounded-2xl bg-white my-auto relative p-4 sm:p-5">
              <div className="text-center mb-3 mt-4 md:mt-0">
                <h2 className="text-base sm:text-lg font-black tracking-[0.05em] text-[#0E3652]">
                  Password Recovery
                </h2>
                <div className="mx-auto mt-0.5 h-[2px] w-12 bg-[#F4B429]"></div>
              </div>

              {error && <p className={`${masterAuthErrorBox} !py-1 !px-2 !text-xs mb-1 font-extrabold`}>{error}</p>}
              {success && <p className={`${masterAuthSuccessBox} !py-1 !px-2 !text-xs mb-1 font-extrabold`}>{success}</p>}

              {/* STEP 1: Enter Registered Email (No Captcha) */}
              {forgotStep === 1 && (
                <form onSubmit={handleForgotRequestOtp} autoComplete="off" className="space-y-3">
                  <div>
                    <label className={`${masterAuthLabel} text-xs mb-0.5 font-black`}>Registered Email Address *</label>
                    <input
                      type="email"
                      required
                      placeholder="Enter your registered email"
                      className={`${masterAuthInput} text-sm font-bold py-2`}
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                    />
                  </div>

                  <button disabled={forgotLoading} className={`${masterAuthSubmitBtn} text-xs font-black uppercase tracking-wider py-2 mt-2`}>
                    <span>{forgotLoading ? "Sending OTP..." : "Send OTP"}</span>
                  </button>
                </form>
              )}

              {/* STEP 2: Enter OTP & View Masked Email (No Captcha) */}
              {forgotStep === 2 && (
                <form onSubmit={handleForgotVerifyOtp} autoComplete="off" className="space-y-3">
                  <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-center">
                    <p className="text-xs text-slate-600 font-bold">OTP sent to email ID:</p>
                    <p className="text-sm font-black text-[#0E3652] tracking-wider mt-0.5">{maskedEmailDisplay}</p>
                  </div>

                  <div>
                    <label className={`${masterAuthLabel} text-xs mb-0.5 font-black`}>Enter 6-Digit OTP *</label>
                    <input
                      type="text"
                      maxLength={6}
                      required
                      placeholder="Enter OTP"
                      className={`${masterAuthInput} text-sm font-bold py-2 tracking-widest text-center`}
                      value={forgotOtp}
                      onChange={(e) => setForgotOtp(e.target.value.replace(/\D/g, ""))}
                    />
                  </div>

                  <button disabled={forgotLoading} className={`${masterAuthSubmitBtn} text-xs font-black uppercase tracking-wider py-2 mt-2`}>
                    <span>{forgotLoading ? "Verifying..." : "Verify OTP"}</span>
                  </button>
                </form>
              )}

              {/* STEP 3: New Password, Confirm Password & Captcha */}
              {forgotStep === 3 && (
                <form onSubmit={handleResetPasswordSubmit} autoComplete="off" className="space-y-3">
                  <div>
                    <label className={`${masterAuthLabel} text-xs mb-0.5 font-black`}>New Password *</label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        required
                        placeholder="••••••••"
                        className={`${masterAuthInput} text-sm font-bold py-2 pr-9`}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword((prev) => !prev)}
                        className="absolute inset-y-0 right-0 flex items-center px-2.5 text-slate-500 hover:text-[#0E3652] cursor-pointer"
                      >
                        {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className={`${masterAuthLabel} text-xs mb-0.5 font-black`}>Confirm New Password *</label>
                    <div className="relative">
                      <input
                        type={showConfirmNewPassword ? "text" : "password"}
                        required
                        placeholder="••••••••"
                        className={`${masterAuthInput} text-sm font-bold py-2 pr-9`}
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmNewPassword((prev) => !prev)}
                        className="absolute inset-y-0 right-0 flex items-center px-2.5 text-slate-500 hover:text-[#0E3652] cursor-pointer"
                      >
                        {showConfirmNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  {/* Captcha - Only on Step 3 */}
                  <div>
                    <label className={`${masterAuthLabel} text-xs mb-0.5 font-black`}>Captcha *</label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="text"
                        required
                        autoComplete="off"
                        placeholder="Enter captcha code"
                        className={`${masterAuthInput} text-sm font-bold py-2`}
                        value={captchaInput}
                        onChange={(e) => setCaptchaInput(e.target.value)}
                      />
                      <div className="flex items-center justify-between bg-slate-100 border border-slate-300 rounded-xl px-2.5 select-none py-1.5 min-w-[115px]">
                        <span className="font-mono font-extrabold tracking-[0.15em] text-[#0E3652] text-sm">{captchaCode}</span>
                        <button type="button" onClick={generateNewCaptcha} className="text-slate-600 hover:text-black cursor-pointer ml-1.5">
                          <RefreshCw size={14} />
                        </button>
                      </div>
                    </div>
                  </div>

                  <button disabled={forgotLoading} className={`${masterAuthSubmitBtn} text-xs font-black uppercase tracking-wider py-2 mt-2`}>
                    <span>{forgotLoading ? "Resetting..." : "Update Password"}</span>
                  </button>
                </form>
              )}

              <div className="text-center mt-3">
                <button
                  type="button"
                  className="font-black text-xs text-[#0E3652] hover:underline cursor-pointer tracking-[0.05em]"
                  onClick={() => {
                    setIsForgotMode(false);
                    setForgotStep(1);
                    setError("");
                    setSuccess("");
                    generateNewCaptcha();
                  }}
                >
                  Back to Sign In
                </button>
              </div>
            </div>
          ) : (
            <div className={`flex w-full max-w-md flex-col rounded-2xl bg-white my-auto relative ${isLoginMode ? "p-4 sm:p-5" : "p-1.5 sm:p-2"}`}>

              {/* Clean Title */}
              <div className={`text-center ${isLoginMode ? "mb-3 mt-4 md:mt-0" : "mb-1 mt-1 md:mt-0"}`}>
                <h2 className="text-base sm:text-lg font-black tracking-[0.05em] text-[#0E3652]">
                  {isLoginMode ? "Student/Institute Login" : "New Account Registration"}
                </h2>
                <div className="mx-auto mt-0.5 h-[2px] w-12 bg-[#F4B429]"></div>
              </div>

              {error && <p className={`${masterAuthErrorBox} !py-1 !px-2 !text-xs mb-1 font-extrabold`}>{error}</p>}
              {success && <p className={`${masterAuthSuccessBox} !py-1 !px-2 !text-xs mb-1 font-extrabold`}>{success}</p>}

              <form onSubmit={handleSubmit} autoComplete="off" className={isLoginMode ? "space-y-3" : "space-y-1"}>
                {/* Hidden dummy inputs to fool browser credential managers and prevent unwanted autofill */}
                <input type="text" style={{ display: 'none' }} autoComplete="username" />
                <input type="password" style={{ display: 'none' }} autoComplete="current-password" />

                {isLoginMode && (
                  <div className="grid grid-cols-2 gap-0 mb-2 bg-slate-100 p-0.5 rounded-xl border border-slate-200">
                    <button
                      type="button"
                      className={`flex items-center justify-center gap-1.5 py-1 text-sm font-extrabold rounded-lg transition-all cursor-pointer tracking-[0.05em] ${
                        loginMethod === "email" ? "bg-[#0E3652] text-white shadow-sm" : "text-[#0E3652] hover:text-slate-900"
                      }`}
                      onClick={() => {
                        setLoginMethod("email");
                        setForm((prev) => ({ ...prev, userid: "", password: "" }));
                        setError("");
                        setSuccess("");
                        generateNewCaptcha();
                      }}
                    >
                      <Mail size={15} /> Email
                    </button>
                    <button
                      type="button"
                      className={`flex items-center justify-center gap-1.5 py-1 text-sm font-extrabold rounded-lg transition-all cursor-pointer tracking-[0.05em] ${
                        loginMethod === "mobile" ? "bg-[#0E3652] text-white shadow-sm" : "text-[#0E3652] hover:text-slate-900"
                      }`}
                      onClick={() => {
                        setLoginMethod("mobile");
                        setForm((prev) => ({ ...prev, userid: "", password: "" }));
                        setError("");
                        setSuccess("");
                        generateNewCaptcha();
                      }}
                    >
                      <Smartphone size={15} /> Mobile
                    </button>
                  </div>
                )}

                {!isLoginMode && (
                  <div className="grid grid-cols-2 gap-1 mb-0.5 bg-slate-100 p-0.5 rounded-lg">
                    <button
                      type="button"
                      className={`py-1 text-xs font-extrabold rounded transition-all cursor-pointer tracking-[0.05em] ${
                        registerRole === "student" ? "bg-[#0E3652] text-white shadow" : "text-[#0E3652]"
                      }`}
                      onClick={() => setRegisterRole("student")}
                    >
                      Student
                    </button>
                    <button
                      type="button"
                      className={`py-1 text-xs font-extrabold rounded transition-all cursor-pointer tracking-[0.05em] ${
                        registerRole === "organization" ? "bg-[#0E3652] text-white shadow" : "text-[#0E3652]"
                      }`}
                      onClick={() => setRegisterRole("organization")}
                    >
                      Organization
                    </button>
                  </div>
                )}

                {!isLoginMode && (
                  <div className="grid grid-cols-2 gap-1">
                    <div>
                      <label className={`${masterAuthLabel} text-[10px] font-black mb-0`}>
                        {registerRole === "organization" ? "Institution Name *" : "Full Name *"}
                      </label>
                      <input
                        type="text"
                        required
                        autoComplete="off"
                        placeholder={registerRole === "organization" ? "e.g., MEDHASHALA ACADEMY" : "e.g., MEDHASHALA ASPIRANT"}
                        className={`${masterAuthInput} text-xs font-bold py-2 w-full`}
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value.toUpperCase() })}
                      />
                    </div>

                    <div>
                      <label className={`${masterAuthLabel} text-[10px] font-black mb-0`}>Mobile No. *</label>
                      <input
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        required
                        autoComplete="off"
                        maxLength={10}
                        placeholder="9876543210"
                        className={`${masterAuthInput} text-xs font-bold py-2 w-full`}
                        value={form.phone}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                          setForm({ ...form, phone: val });
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Email / User ID field with Verify Button */}
                <div>
                  <label className={`${masterAuthLabel} ${isLoginMode ? "text-xs mb-0.5 font-black" : "text-[10px] mb-0 font-black"}`}>
                    {isLoginMode ? (loginMethod === "mobile" ? "Mobile Number *" : "Email *") : "Email Address *"}
                  </label>
                  <div className="flex gap-2">
                    <input
                      type={isLoginMode && loginMethod === "mobile" ? "tel" : "text"}
                      inputMode={isLoginMode && loginMethod === "mobile" ? "numeric" : "text"}
                      required
                      autoComplete="off"
                      disabled={!isLoginMode && isEmailVerified}
                      placeholder={isLoginMode ? (loginMethod === "mobile" ? "Enter 10-digit mobile no." : "Enter email address") : "Enter email address"}
                      className={`${masterAuthInput} ${isLoginMode ? "text-base font-bold py-3 px-4 w-full" : "text-xs font-bold py-1 w-full"}`}
                      value={form.userid}
                      onChange={(e) => {
                        const val = isLoginMode && loginMethod === "mobile" ? e.target.value.replace(/\D/g, "").slice(0, 10) : e.target.value;
                        setForm({ ...form, userid: val });
                        if (!isLoginMode) {
                          setIsEmailVerified(false);
                          setShowOtpInput(false);
                        }
                      }}
                    />
                    {!isLoginMode && (
                      <button
                        type="button"
                        onClick={handleSendEmailOtp}
                        disabled={verifyingEmail || isEmailVerified}
                        className="px-2.5 py-2 bg-[#0E3652] hover:bg-[#154668] text-white text-xs font-bold rounded-xl transition-all disabled:opacity-50 cursor-pointer tracking-[0.05em] shrink-0"
                      >
                        {isEmailVerified ? "Verified ✓" : verifyingEmail ? "Sending..." : "Verify"}
                      </button>
                    )}
                  </div>

                  {/* OTP verification input row */}
                  {!isLoginMode && showOtpInput && !isEmailVerified && (
                    <div className="flex gap-2 mt-1.5">
                      <input
                        type="text"
                        maxLength={6}
                        placeholder="Enter 6-digit OTP"
                        className={`${masterAuthInput} text-xs font-bold py-1`}
                        value={emailOtp}
                        onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, ""))}
                      />
                      <button
                        type="button"
                        onClick={handleVerifyEmailOtp}
                        disabled={verifyingEmail}
                        className="px-3 py-1 bg-[#F4B429] hover:bg-[#e0a320] text-[#0E3652] text-xs font-extrabold rounded-xl transition-all cursor-pointer shrink-0"
                      >
                        Confirm OTP
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <div className={`flex items-center justify-between ${isLoginMode ? "mb-0.5" : "mb-0"}`}>
                    <label className={`${masterAuthLabel} ${isLoginMode ? "text-xs font-black" : "text-[10px] font-black"}`}>Password *</label>
                    {isLoginMode && (
                      <button 
                        type="button" 
                        onClick={() => {
                          setIsForgotMode(true);
                          setError("");
                          setSuccess("");
                          generateNewCaptcha();
                        }} 
                        className="text-xs font-bold text-[#0E3652] hover:underline cursor-pointer tracking-[0.05em]"
                      >
                        Forgot Password?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      required
                      autoComplete="new-password"
                      placeholder="••••••••"
                      className={`${masterAuthInput} ${isLoginMode ? "text-base font-bold py-3 px-4 pr-11 w-full" : "text-xs font-bold py-2 pr-8 w-full"} `}
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-500 hover:text-[#0E3652] cursor-pointer"
                    >
                      {showPassword ? <EyeOff size={isLoginMode ? 18 : 14} /> : <Eye size={isLoginMode ? 18 : 14} />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password field */}
                {!isLoginMode && (
                  <div>
                    <div className="flex items-center justify-between mb-0">
                      <label className={`${masterAuthLabel} text-[10px] font-black mb-0`}>Confirm Password *</label>
                      {form.confirmPassword && form.password !== form.confirmPassword && (
                        <span className="text-[10px] font-bold text-rose-600 tracking-[0.05em]">Passwords do not match</span>
                      )}
                    </div>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        required
                        autoComplete="new-password"
                        placeholder="••••••••"
                        className={`${masterAuthInput} text-xs font-bold py-2 pr-8 w-full ${
                          form.confirmPassword && form.password !== form.confirmPassword 
                            ? "!border-rose-300 focus:!border-rose-500 focus:!ring-rose-500/20" 
                            : ""
                        }`}
                        value={form.confirmPassword}
                        onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        className="absolute inset-y-0 right-0 flex items-center px-2 text-slate-500 hover:text-[#0E3652] cursor-pointer"
                      >
                        {showConfirmPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  </div>
                )}
            
                {!isLoginMode && registerRole === "student" && (
                  <div className="space-y-0.5 pt-0.5 border-t border-slate-200">
                    <div className="flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        id="belongsInst"
                        checked={belongsToInst}
                        onChange={(e) => {
                          setBelongsToInst(e.target.checked);
                          if (!e.target.checked) {
                            setIsReferralVerified(false);
                            setVerifiedTenantKey("");
                            setOrgExams([]);
                          }
                        }}
                        className="h-3 w-3 rounded border-slate-300 text-[#0E3652] focus:ring-0 cursor-pointer"
                      />
                      <label htmlFor="belongsInst" className="text-[11px] text-[#0E3652] cursor-pointer font-bold tracking-[0.05em]">
                        Do you belong to any Institution?
                      </label>
                    </div>

                    {belongsToInst && (
                      <div className="space-y-0">
                        <label className={`${masterAuthLabel} text-[10px] font-black mb-0`}>Institution Referral Code <span className="text-red-500">*</span></label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            required={belongsToInst}
                            autoComplete="off"
                            placeholder="e.g., MEDHA01"
                            disabled={isReferralVerified}
                            className={`${masterAuthInput} text-xs font-bold py-1 w-full`}
                            value={form.referredBy}
                            onChange={(e) => {
                              setForm({ ...form, referredBy: e.target.value.toUpperCase() });
                              setIsReferralVerified(false);
                            }}
                          />
                          <button
                          type="button"
                          onClick={handleVerifyReferral}
                          disabled={verifyingCode || isReferralVerified}
                          className="px-2.5 py-1 bg-[#0E3652] hover:bg-[#154668] text-white text-xs font-bold rounded-xl transition-all disabled:opacity-50 cursor-pointer tracking-[0.05em] shrink-0"
                        >
                          {verifyingCode ? "Verifying..." : isReferralVerified ? "Verified ✓" : "Verify"}
                        </button>
                      </div>
                   {isReferralVerified && (
                        <p className="text-[10px] text-emerald-600 font-bold mt-1 tracking-[0.05em]">
                          Verified Institute: <span className="font-black">{verifiedOrgName}</span>
                        </p>
                      )}
                    </div>
                  )}
                  </div>
                )}

                {!isLoginMode && registerRole === "student" && (!belongsToInst || isReferralVerified) && (
                  <div className="space-y-0">
                    <label className={`${masterAuthLabel} text-[10px] font-black mb-0`}>
                      Targeted Govt Exams <span className="text-[9px] font-normal normal-case text-slate-500">(Optional)</span>
                    </label>
                    <div className="max-h-12 overflow-y-auto custom-scrollbar grid grid-cols-2 gap-1 p-1 bg-slate-50 border border-slate-200 rounded-lg">
                      {(belongsToInst ? orgExams : examsMaster).length > 0 ? (
                        (belongsToInst ? orgExams : examsMaster).map((exam) => {
                          const isChecked = form.selectedExams.includes(exam.examCode);
                          return (
                            <button
                              key={exam.examCode}
                              type="button"
                              onClick={() => handleExamSelect(exam.examCode)}
                              className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] text-left transition-all cursor-pointer tracking-[0.05em] ${
                                isChecked ? "bg-[#0E3652] text-white font-bold" : "bg-white text-[#0E3652] hover:bg-slate-100 border border-slate-200 font-semibold"
                              }`}
                            >
                              <div className={`flex h-2 w-2 shrink-0 items-center justify-center rounded border text-[6px] ${isChecked ? "border-[#F4B429] bg-[#F4B429] font-black text-[#0E3652]" : "border-slate-400"}`}>
                                {isChecked ? "✓" : ""}
                              </div>
                              <span className="truncate">{exam.examName}</span>
                            </button>
                          );
                        })
                      ) : (
                        <div className="col-span-2 py-0.5 text-center text-[10px] text-slate-500 tracking-[0.05em] font-semibold">
                          {belongsToInst && !isReferralVerified 
                            ? "Verify institution referral code above." 
                            : "No exams found for this institution."}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Clean Captcha input */}
                <div>
                  <label className={`${masterAuthLabel} ${isLoginMode ? "text-xs mb-0.5 font-black" : "text-[10px] mb-0 font-black"}`}>Captcha *</label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="text"
                      required
                      autoComplete="off"
                      placeholder="Enter captcha code"
                      className={`${masterAuthInput} ${isLoginMode ? "text-base font-bold py-3 px-4 w-full" : "text-xs font-bold py-1 w-full"}`}
                      value={captchaInput}
                      onChange={(e) => setCaptchaInput(e.target.value)}
                    />
                    <div className={`flex items-center justify-between bg-slate-100 border border-slate-300 rounded-xl px-2.5 select-none ${isLoginMode ? "py-3 min-w-[115px]" : "py-1 min-w-[100px]"}`}>
                      <span className={`font-mono font-extrabold tracking-[0.15em] text-[#0E3652] ${isLoginMode ? "text-base" : "text-xs"}`}>{captchaCode}</span>
                      <button type="button" onClick={generateNewCaptcha} className="text-slate-600 hover:text-black cursor-pointer ml-1.5">
                        <RefreshCw size={isLoginMode ? 16 : 12} />
                      </button>
                    </div>
                  </div>
                </div>
                <button 
                  disabled={isLoading} 
                  className={`${masterAuthSubmitBtn} text-xs font-black uppercase tracking-wider ${isLoginMode ? "py-3 mt-3 w-full" : "py-2 mt-0.5 w-full"} flex items-center justify-center gap-2`}
                >
                  {isLoading && (
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                    </svg>
                  )}
                  <span>{isLoading ? "Signing in..." : (isLoginMode ? "Login" : "Create Account")}</span>
                </button>
              </form>

              <div className={`text-center ${isLoginMode ? "mt-3" : "mt-1.5"}`}>
                <p className="text-xs text-slate-700 tracking-[0.05em] font-bold">
                  {isLoginMode ? "New to Medhashala?" : "Already Registered?"}{" "}
                  <button
                    type="button"
                    className="font-black text-[#0E3652] hover:underline cursor-pointer tracking-[0.05em]"
                    onClick={() => {
                      setIsLoginMode(!isLoginMode);
                      setError("");
                      setSuccess("");
                      setShowPassword(false);
                      setShowConfirmPassword(false);
                      setIsReferralVerified(false);
                      setIsEmailVerified(false);
                      setShowOtpInput(false);
                      setEmailOtp("");
                      setVerifiedTenantKey("");
                      setOrgExams([]);
                      generateNewCaptcha();
                    }}
                  >
                    {isLoginMode ? "Register Now" : "Sign In"}
                  </button>
                </p>
              </div>

              <div className={`border-t border-slate-100 text-center ${isLoginMode ? "mt-3 pt-2" : "mt-1 pt-0.5"}`}>
                <p className="text-[10px] leading-tight text-red-600 font-bold tracking-[0.05em]">
                  Note : If you encounter any technical issue, e-mail us: <span className="font-black">support.medhashala@gmail.com</span>
                </p>
              </div>
          </div>
        )}
      </div>
    </div>
  </div>
);
};

export default Login;